import { z } from 'zod'
import { MUSCLE_GROUP_COLORS } from '../../utils/muscle-groups'
import { applyEWMA, calculate1RM } from '../analysis'
import type { AITool } from './types'

const parametersSchema = z.object({
  graphType: z.enum(['exercise_progress', 'bodyweight_trend', 'volume_trend', 'workout_frequency'])
    .describe('Type of graph to generate'),
  
  // For exercise_progress:
  exerciseTemplateId: z.string()
    .describe('Exercise template ID (required for exercise_progress, use empty string for others)'),
  
  // For volume_trend:
  muscleGroup: z.string()
    .describe('Muscle group name (required for volume_trend, use empty string for others)'),
  
  // Date range:
  startDate: z.string().describe('Start date in YYYY-MM-DD format'),
  endDate: z.string().describe('End date in YYYY-MM-DD format'),
  
  // Visual options:
  smoothed: z.boolean().describe('Whether to apply smoothing to the trend line'),
  color: z.string().describe('Chart color (muscle group name or hex code, use empty string for default)'),
  title: z.string().describe('Custom chart title (use empty string for auto-generated title)')
})

export const generateGraph: AITool = {
  name: 'generateGraph',
  description: 'Generate interactive line chart with automatic data fetching.',
  parameters: parametersSchema,

  execute: async (args, context) => {
    const { graphType, exerciseTemplateId, muscleGroup, startDate, endDate, smoothed, color, title } = args
    const { workoutData, weightData, userSettings } = context
    
    const unitSystem = userSettings?.settings?.units || 'metric'
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    // Convert empty strings to undefined for optional fields
    const templateId = exerciseTemplateId || undefined
    const muscle = muscleGroup || undefined
    let chartColor = color || MUSCLE_GROUP_COLORS.lats
    let chartTitle = title || ''
    
    let chartData: Array<{ x: string; y: number }> = []
    let exerciseName = ''
    let yLabel = ''
    
    switch (graphType) {
      case 'exercise_progress':
        if (!templateId) {
          return { success: false, error: 'exerciseTemplateId required for exercise_progress' }
        }
        
        // Find exercise sessions
        const exerciseSessions: Array<{ date: Date; sets: Array<{ weight_kg: number; reps: number }> }> = []
        
        workoutData.workouts?.forEach(workout => {
          const workoutDate = new Date(workout.start_time)
          if (workoutDate >= start && workoutDate <= end) {
            const exercisesInWorkout = workout.exercises?.filter(ex => ex.exercise_template_id === templateId) || []
            
            if (exercisesInWorkout.length > 0) {
              if (!exerciseName) exerciseName = exercisesInWorkout[0].title
              
              const allSets = exercisesInWorkout.flatMap(ex => 
                ex.sets?.map((set: any) => ({
                  weight_kg: set.weight_kg || 0,
                  reps: set.reps || 0
                })) || []
              )
              
              if (allSets.length > 0) {
                exerciseSessions.push({ date: workoutDate, sets: allSets })
              }
            }
          }
        })
        
        if (exerciseSessions.length === 0) {
          return { success: false, error: 'No exercise data found in date range' }
        }
        
        // Calculate 1RM for each session
        exerciseSessions.forEach(session => {
          let best1RM = 0
          let isBodyweight = true
          
          session.sets.forEach(set => {
            if (set.weight_kg > 0) isBodyweight = false
            const est1RM = set.weight_kg === 0 ? set.reps : calculate1RM(set.weight_kg, set.reps)
            if (est1RM > best1RM) best1RM = est1RM
          })
          
          // Convert to user's preferred units
          const displayValue = isBodyweight ? best1RM : best1RM * (unitSystem === 'metric' ? 1 : 2.20462)
          
          chartData.push({
            x: session.date.toISOString().split('T')[0],
            y: displayValue
          })
        })
        
        chartTitle = chartTitle || `${exerciseName} Progress`
        yLabel = exerciseName.includes('(') ? 'Reps' : `Weight (${unitSystem === 'metric' ? 'kg' : 'lbs'})`
        break
        
      case 'bodyweight_trend':
        if (!weightData || weightData.length === 0) {
          return { success: false, error: 'No bodyweight data available' }
        }
        
        chartData = (weightData || [])
          .filter(w => {
            const date = new Date(w.date)
            return date >= start && date <= end
          })
          .map(w => ({
            x: typeof w.date === 'string' ? w.date : w.date.toISOString().split('T')[0],
            y: w.weight * (unitSystem === 'metric' ? 1 : 2.20462)
          }))
        
        chartTitle = chartTitle || 'Bodyweight Trend'
        yLabel = `Weight (${unitSystem === 'metric' ? 'kg' : 'lbs'})`
        break
        
      case 'volume_trend':
        if (!muscle) {
          return { success: false, error: 'muscleGroup required for volume_trend' }
        }
        
        // Group workouts by date and calculate volume
        const volumeByDate = new Map<string, number>()
        
        workoutData.workouts?.forEach(workout => {
          const workoutDate = new Date(workout.start_time)
          if (workoutDate >= start && workoutDate <= end) {
            const dateKey = workoutDate.toISOString().split('T')[0]
            let dailyVolume = 0
            
            workout.exercises?.forEach(exercise => {
              if (exercise.primaryMover === muscle) {
                exercise.sets?.forEach((set: any) => {
                  const volume = (set.weight_kg || 0) * (set.reps || 0)
                  dailyVolume += volume * (unitSystem === 'metric' ? 1 : 2.20462)
                })
              }
            })
            
            volumeByDate.set(dateKey, (volumeByDate.get(dateKey) || 0) + dailyVolume)
          }
        })
        
        chartData = Array.from(volumeByDate.entries())
          .map(([date, volume]) => ({ x: date, y: volume }))
          .sort((a, b) => a.x.localeCompare(b.x))
        
        chartTitle = chartTitle || `${muscle} Volume Trend`
        yLabel = `Volume (${unitSystem === 'metric' ? 'kg' : 'lbs'})`
        chartColor = MUSCLE_GROUP_COLORS[muscle.toLowerCase() as keyof typeof MUSCLE_GROUP_COLORS] || chartColor
        break
        
      case 'workout_frequency':
        // Group workouts by week
        const weeklyWorkouts = new Map<string, number>()
        
        workoutData.workouts?.forEach(workout => {
          const workoutDate = new Date(workout.start_time)
          if (workoutDate >= start && workoutDate <= end) {
            const weekStart = new Date(workoutDate)
            weekStart.setDate(workoutDate.getDate() - workoutDate.getDay())
            const weekKey = weekStart.toISOString().split('T')[0]
            
            weeklyWorkouts.set(weekKey, (weeklyWorkouts.get(weekKey) || 0) + 1)
          }
        })
        
        chartData = Array.from(weeklyWorkouts.entries())
          .map(([date, count]) => ({ x: date, y: count }))
          .sort((a, b) => a.x.localeCompare(b.x))
        
        chartTitle = chartTitle || 'Weekly Workout Frequency'
        yLabel = 'Workouts per Week'
        break
    }
    
    if (chartData.length === 0) {
      return { success: false, error: 'No data found for the specified parameters' }
    }
    
    // Sort by date
    chartData.sort((a, b) => a.x.localeCompare(b.x))
    
    // Apply smoothing if requested
    let processedData = chartData
    if (smoothed) {
      const yValues = chartData.map(d => d.y)
      const smoothedValues = applyEWMA(yValues, 0.3)
      processedData = chartData.map((d, i) => ({
        ...d,
        smoothed: smoothedValues[i]
      }))
    }
    
    // Resolve color
    if (color && color.toLowerCase() in MUSCLE_GROUP_COLORS) {
      chartColor = MUSCLE_GROUP_COLORS[color.toLowerCase() as keyof typeof MUSCLE_GROUP_COLORS]
    } else if (color && /^#[0-9A-F]{6}$/i.test(color)) {
      chartColor = color
    }
    
    // Create chart configuration
    const chartConfig = {
      type: 'chart' as const,
      data: processedData,
      color: chartColor,
      smoothed,
      title: chartTitle,
      x_label: 'Date',
      y_label: yLabel,
      dataKeys: {
        x: 'x',
        y: smoothed ? 'smoothed' : 'y',
        raw: smoothed ? 'y' : undefined
      }
    }
    
    // Calculate trend
    const firstValue = chartData[0].y
    const lastValue = chartData[chartData.length - 1].y
    const changePercent = firstValue !== 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0
    
    let trendDescription = 'stable'
    if (Math.abs(changePercent) > 5) {
      trendDescription = changePercent > 0 ? 'increasing' : 'decreasing'
    }
    
    const summary = `Generated ${chartTitle} with ${chartData.length} data points. Trend: ${trendDescription} (${changePercent > 0 ? '+' : ''}${changePercent.toFixed(1)}%).`
    
    return {
      type: 'chart',
      chartConfig,
      success: true,
      chartGenerated: true,
      graphType,
      dataPointsCount: chartData.length,
      timeframe: `${chartData[0].x} to ${chartData[chartData.length - 1].x}`,
      summary
    }
  }
}